/**
 * What the check-in surfaces offer to print for one device, and where it goes.
 *
 * Completing a standard check-in already prints without anyone pressing
 * anything: sm4rt-api fires the `check_in.device_checked_in` AutoPrint trigger
 * per device, and the seeded bindings send two job labels to the counter and one
 * A4 job sheet to the office laser. These buttons are the manual half — the
 * reprint after a jam, a serial correction, or a second machine that arrives
 * later — so **one press is one document**, not another pair.
 *
 * Two rules live here rather than in the components, because both surfaces (the
 * session page's Print section and the device page's Done summary) have to agree
 * on them:
 *
 * - **Standard intake prints downstairs only.** The auto-print already went to
 *   the counter and the customer is standing at it, so the floor isn't a
 *   decision worth making twice.
 * - **Express offers both floors.** An express session completes into
 *   `creating_jobs`: its RepairShopr tickets are created by the orchestrator
 *   *after* the session closes, so nothing auto-printed and the machine may well
 *   be upstairs on the bench by the time there is a ticket to name. Which floor
 *   the label belongs on is then the operator's call, exactly as it is on the
 *   technician card.
 *
 * Only the labels name a lane. The A4 sheet has one printer that can render it,
 * resolved server-side from the document (`DOCUMENT_DEFAULT_ROLES` in
 * sm4rt-api's `print_service.py`) — a client guessing wrong doesn't print small,
 * it dead-letters on the agent with nothing on screen to say so.
 */

import type { CheckInDraftJob, CheckInSession } from '@techgitter/contracts';

import { LABEL_LANE_COUNTER, LABEL_LANE_WORKSHOP } from '../config/printLanes';

export type CheckInPrintAction =
  | 'job-label-upstairs'
  | 'job-label-downstairs'
  | 'ticket-sheet';

export interface CheckInPrintButton {
  action: CheckInPrintAction;
  /** Visible text. The two job-label buttons share it and are told apart by
   *  the arrow glyph, so `title` is what actually names them. */
  label: string;
  /** Accessible name + hover text. */
  title: string;
  /** Printer lane for a label; absent for the A4 sheet, whose lane sm4rt-api
   *  resolves from the document. */
  role?: string;
  /** Set when the action can't run. The button is disabled and this says why —
   *  a row that reshuffles per device costs more than a greyed button. */
  unavailableReason?: string;
}

/** Shown on every button of a device whose RepairShopr ticket doesn't exist
 *  yet. Both documents are keyed to a ticket, so neither can render without
 *  one — an express device between completion and `rs_create_tickets` is the
 *  case that hits this. */
export const NO_TICKET_REASON =
  'No RepairShopr ticket on this device yet — it is created when the check-in is submitted';

const JOB_LABEL_UPSTAIRS: CheckInPrintButton = {
  action: 'job-label-upstairs',
  label: 'Job label',
  title: 'Print the job label upstairs (workshop)',
  role: LABEL_LANE_WORKSHOP,
};

const JOB_LABEL_DOWNSTAIRS: CheckInPrintButton = {
  action: 'job-label-downstairs',
  label: 'Job label',
  title: 'Print the job label downstairs (counter)',
  role: LABEL_LANE_COUNTER,
};

const TICKET_SHEET: CheckInPrintButton = {
  action: 'ticket-sheet',
  label: 'Ticket sheet',
  title: 'Print the A4 job ticket sheet on the office printer',
};

/** True for the intake type whose tickets are created after the session closes. */
export function offersBothFloors(session: CheckInSession): boolean {
  return (session.intakeType ?? '').toLowerCase() === 'express';
}

/**
 * The print buttons for one device, in fixed order: label(s) first, then the
 * sheet. Each carries its lane and, when the device has no ticket, the reason
 * it can't run.
 */
export function checkInPrintButtons(
  session: CheckInSession,
  draftJob: CheckInDraftJob,
): CheckInPrintButton[] {
  const unavailableReason = draftJob.repairShoprTicketId
    ? undefined
    : NO_TICKET_REASON;
  const buttons = offersBothFloors(session)
    ? [JOB_LABEL_UPSTAIRS, JOB_LABEL_DOWNSTAIRS, TICKET_SHEET]
    : [JOB_LABEL_DOWNSTAIRS, TICKET_SHEET];
  return buttons.map(button =>
    unavailableReason ? { ...button, unavailableReason } : button,
  );
}
